import { getProcessingReceipt } from './processingReceiptModel';
export const timestampToMillis = (value = null) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getCareSourceRecordedAtMillis = (source = {}) => (
  timestampToMillis(source.capturedAt || source.createdAt || source.updatedAt)
);

export const formatCareSourceRecordedAt = (source = {}) => {
  const millis = getCareSourceRecordedAtMillis(source);
  if (!millis) return '';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(millis));
};

const formatLocalDateKey = (dateKey = '') => {
  const match = String(dateKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const formatCareSourceTimelineDateSummary = (source = {}) => {
  const dateLabels = [
    ...(Array.isArray(source.savedTimelineDateLabels) ? source.savedTimelineDateLabels : []),
    ...buildCareSourceTimelineDateLabels(getCareSourceTimelinePointerDateKeys(source)),
  ].filter(Boolean);

  if (!dateLabels.length) return '';
  if (dateLabels.length === 1) return `Logged for: ${dateLabels[0]}`;
  if (dateLabels.length === 2) return `Logged for: ${dateLabels[0]} and ${dateLabels[1]}`;

  return `Logged for: ${dateLabels[0]} and ${dateLabels.length - 1} more`;
};

export const buildCareSourceTimelineDateLabels = (dateKeys = []) => (
  [...new Set(dateKeys)]
    .map(formatLocalDateKey)
    .filter(Boolean)
);

const COMPLETED_RECEIPT_STATUSES = new Set([
  'organized',
  'needs_review',
  'partially_organized',
  'reviewed',
  'saved_as_original_note',
  'saved_source',
]);

const FAILED_REVIEW_STATUSES = new Set([
  'needs_review',
  'partially_organized',
  'extraction_failed',
]);

const getCareSourceStatusValue = (source = {}) => (
  String(source.status || source.metadata?.careSourceProcessing?.status || '').trim()
);

const normalizeSavedTimelineCards = (cards = []) => (
  (Array.isArray(cards) ? cards : [])
    .map((card) => ({
      entryId: String(card?.entryId || '').trim(),
      plannerCardKey: String(card?.plannerCardKey || '').trim(),
      localDate: String(card?.localDate || '').trim(),
    }))
    .filter((card) => card.entryId || card.plannerCardKey)
);

const normalizePointerIds = (ids = []) => (
  (Array.isArray(ids) ? ids : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
);

const normalizePointerDateKeys = (dateKeys = []) => (
  (Array.isArray(dateKeys) ? dateKeys : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
);

export const getCareSourceSavedTimelineCards = (source = {}) => {
  const careSourceCards = normalizeSavedTimelineCards(source.metadata?.careSourceProcessing?.savedTimelineCards);
  if (careSourceCards.length) return careSourceCards;

  const captureFirstCards = normalizeSavedTimelineCards(source.metadata?.captureFirst?.savedTimelineCards);
  if (captureFirstCards.length) return captureFirstCards;

  return [];
};

export const getCareSourceTimelinePointerEntryIds = (source = {}) => {
  const careSourceIds = normalizePointerIds(source.metadata?.careSourceProcessing?.timelinePointerEntryIds);
  if (careSourceIds.length) return careSourceIds;

  return [];
};

export const getCareSourceTimelinePointerDateKeys = (source = {}) => {
  const careSourceDateKeys = normalizePointerDateKeys(source.metadata?.careSourceProcessing?.timelinePointerDateKeys);
  if (careSourceDateKeys.length) return careSourceDateKeys;

  return [];
};

export const getCareSourceTimelinePointerCount = (source = {}) => {
  const explicitCount = Number(source.metadata?.careSourceProcessing?.timelinePointerCount || 0);
  if (explicitCount > 0) return explicitCount;

  const pointerIds = getCareSourceTimelinePointerEntryIds(source);
  if (pointerIds.length) return pointerIds.length;

  return getCareSourceTimelinePointerDateKeys(source).length;
};

export const getCareSourcePlannerCardKeys = (source = {}) => {
  const savedTimelineCards = getCareSourceSavedTimelineCards(source);
  if (savedTimelineCards.length) {
    return [...new Set(savedTimelineCards
      .map((card) => card.plannerCardKey)
      .filter(Boolean))];
  }

  const careSourceKeys = Array.isArray(source.metadata?.careSourceProcessing?.plannerCardKeys)
    ? source.metadata.careSourceProcessing.plannerCardKeys
    : [];
  const captureFirstKeys = Array.isArray(source.metadata?.captureFirst?.plannerCardKeys)
    ? source.metadata.captureFirst.plannerCardKeys
    : [];
  const plannerCardKeys = [...new Set([...careSourceKeys, ...captureFirstKeys]
    .map((value) => String(value || '').trim())
    .filter(Boolean))];

  return plannerCardKeys;
};

export const getCareSourceSavedRecordCount = (source = {}) => {
  const plannerCardKeys = getCareSourcePlannerCardKeys(source);
  if (plannerCardKeys.length) {
    return plannerCardKeys.length;
  }

  return Number(
    source.extractionSummary?.savedPlannerCardCount
    || source.extractionSummary?.savedRecordCount
    || source.metadata?.careSourceProcessing?.timelineEntryIds?.length
    || source.metadata?.captureFirst?.timelineEntryIds?.length
    || 0
  );
};

const getCareSourceTimelineActivityCount = (source = {}) => (
  getCareSourceSavedRecordCount(source)
  + getCareSourceTimelinePointerCount(source)
);

export const hasCareSourceSavedTimelineRecords = (source = {}) => (
  getCareSourceTimelineActivityCount(source) > 0
);

const hasProcessingError = (source = {}) => Boolean(
  source.extractionError
  || source.metadata?.captureFirst?.error
  || source.metadata?.careSourceProcessing?.error
);

const getProcessingError = (source = {}) => (
  source.extractionError
  || source.metadata?.captureFirst?.error
  || source.metadata?.careSourceProcessing?.error
  || source.sourceMetadata?.extractionError
  || null
);

const getTextExtractionStatus = (source = {}) => String(
  source.textExtractionStatus
  || source.sourceMetadata?.textExtractionStatus
  || source.metadata?.source?.textExtractionStatus
  || ''
).trim();

export const getCareSourceContext = (source = {}) => String(
  source.sourceContext
  || source.sourceMetadata?.sourceContext
  || source.metadata?.source?.sourceContext
  || ''
).trim();

export const didCareSourceFinishWithoutTimelineRecords = (source = {}) => {
  const status = getCareSourceStatusValue(source);
  const error = getProcessingError(source);
  const code = String(error?.code || '').trim();

  return (
    (
      status === 'saved_as_original_note'
      || (
        status === 'extraction_failed'
        && code === 'ai-extraction-failed'
      )
    )
    && getCareSourceTimelineActivityCount(source) < 1
  );
};

const didProcessingFailBeforeSaving = (source = {}) => {
  if (getCareSourceTimelineActivityCount(source) > 0) {
    return false;
  }

  if (didCareSourceFinishWithoutTimelineRecords(source)) {
    return false;
  }

  const status = getCareSourceStatusValue(source);
  return (
    status === 'extraction_failed'
    || (
      status === 'needs_review'
      && getCareSourceTimelineActivityCount(source) < 1
      && hasProcessingError(source)
    )
  );
};

export const getCareSourceFailureReason = (source = {}) => {
  if (didCareSourceFinishWithoutTimelineRecords(source)) {
    return '';
  }

  if (!didProcessingFailBeforeSaving(source)) {
    return '';
  }

  const error = getProcessingError(source);
  const code = String(error?.code || '').trim();
  const message = String(error?.message || '').trim();

  if (code === 'ai-extraction-failed') {
    return "We couldn't find clear timeline records in this source.";
  }

  if (code === 'invalid-argument') {
    return message || 'The source was missing information needed for processing.';
  }

  if (code === 'not-found') {
    return 'We could not find the child profile for this source.';
  }

  if (code === 'permission-denied') {
    return 'You do not have access to process this source.';
  }

  return message || "We couldn't organize this source right now.";
};

export const sortCareSourcesByNewest = (sources = []) => (
  [...sources].sort((left, right) => (
    getCareSourceRecordedAtMillis(right)
    - getCareSourceRecordedAtMillis(left)
  ))
);

export const getCareSourceDisplayStatus = (source = {}) => {
  const receipt = getProcessingReceipt(source);
  if (receipt.failed) return { label: receipt.saved || receipt.suggested ? "Partly processed" : "Failed", tone: "warning", detail: receipt.error || "Some processing did not finish. Your original is saved." };
  const status = getCareSourceStatusValue(source);
  const savedCount = getCareSourceSavedRecordCount(source);
  const timelinePointerCount = getCareSourceTimelinePointerCount(source);
  const timelineActivityCount = savedCount + timelinePointerCount;
  const profileSuggestionCount = Number(source.profileSuggestionSummary?.suggestionCount || 0);
  const medicationSuggestionCount = Number(
    source.medicationSuggestionSummary?.suggestionCount
    || source.suggestedMedicationUpdates?.length
    || 0
  );
  const careKnowledgeItemCount = Number(
    source.careKnowledgeSuggestionSummary?.itemCount
    || source.suggestedCareKnowledgeItems?.length
    || 0
  );
  const sourceContext = getCareSourceContext(source);
  const textExtractionStatus = getTextExtractionStatus(source);

  if (
    textExtractionStatus === 'pending'
    && (status === 'captured' || status === 'organizing')
  ) {
    return {
      label: 'Reading text',
      tone: 'info',
      detail: "The original source is saved. We're reading the text now.",
    };
  }

  if (status === 'organized') {
    if (careKnowledgeItemCount > 0) {
      return {
        label: 'Needs your review',
        tone: 'warning',
        detail: `${careKnowledgeItemCount} care detail${careKnowledgeItemCount === 1 ? '' : 's'} ready. Original source is saved.`,
      };
    }

    if (savedCount < 1 && timelinePointerCount < 1) {
      return {
        label: 'Saved',
        tone: 'neutral',
        detail: 'The original source is saved. No timeline records were added.',
      };
    }

    if (savedCount < 1 && timelinePointerCount > 0) {
      return {
        label: 'Saved to timeline',
        tone: 'success',
        detail: `${timelinePointerCount} timeline pointer${timelinePointerCount === 1 ? '' : 's'} saved.`,
      };
    }

    return {
      label: 'Saved to timeline',
      tone: 'success',
      detail: `${savedCount} timeline record${savedCount === 1 ? '' : 's'} saved.`,
    };
  }

  if (status === 'saved_as_original_note') {
    return {
      label: 'Saved as one note',
      tone: 'success',
      detail: "We couldn't split this into separate timeline records, so we saved the original note as-is.",
    };
  }

  if (status === 'reviewed') {
    return {
      label: 'Reviewed',
      tone: 'success',
      detail: sourceContext === 'medication'
        ? 'Medication details reviewed. The medication list is updated.'
        : 'This source has been reviewed.',
    };
  }

  if (status === 'saved_source') {
    if (textExtractionStatus === 'pending') {
      return {
        label: 'Saved',
        tone: 'neutral',
        detail: 'The original source is saved. Text extraction will be added next.',
      };
    }
    if (textExtractionStatus === 'failed') {
      return {
        label: 'Saved',
        tone: 'neutral',
        detail: 'The original source is saved, but we could not read text from it yet.',
      };
    }
    if (sourceContext === 'medication') {
      return {
        label: 'Saved',
        tone: 'neutral',
        detail: 'The original medication source is saved for review.',
      };
    }

    return {
      label: 'Saved',
      tone: 'neutral',
      detail: 'The original source is saved for this area.',
    };
  }

  if (didProcessingFailBeforeSaving(source)) {
    return {
      label: 'Failed',
      tone: 'warning',
      detail: "We saved the original source, but couldn't find clear timeline records to add.",
    };
  }

  if (didCareSourceFinishWithoutTimelineRecords(source)) {
    return {
      label: 'Saved',
      tone: 'neutral',
      detail: 'The original source is saved. No timeline records were added.',
    };
  }

  if (status === 'needs_review' || status === 'partially_organized') {
    if (careKnowledgeItemCount > 0) {
      return {
        label: 'Needs your review',
        tone: 'warning',
        detail: `${careKnowledgeItemCount} care detail${careKnowledgeItemCount === 1 ? '' : 's'} ready. Original source is saved.`,
      };
    }

    if (profileSuggestionCount > 0) {
      return {
        label: 'Needs your review',
        tone: 'warning',
        detail: `${profileSuggestionCount} profile detail${profileSuggestionCount === 1 ? '' : 's'} ready. Original source is saved.`,
      };
    }

    if (sourceContext === 'medication' && medicationSuggestionCount > 0) {
      return {
        label: 'Needs your review',
        tone: 'warning',
        detail: `${medicationSuggestionCount} medication detail${medicationSuggestionCount === 1 ? '' : 's'} found. Review before updating the medication list.`,
      };
    }

    if (sourceContext === 'medication') {
      return {
        label: 'Needs your review',
        tone: 'warning',
        detail: 'Please check the source before updating the medication list.',
      };
    }

    return {
      label: 'Needs your review',
      tone: 'warning',
      detail: timelineActivityCount
        ? (savedCount > 0
          ? `${savedCount} timeline record${savedCount === 1 ? '' : 's'} saved.`
          : `${timelinePointerCount} timeline pointer${timelinePointerCount === 1 ? '' : 's'} saved.`)
        : 'The original source is saved and needs your review.',
    };
  }

  return {
    label: 'Processing',
    tone: 'info',
    detail: sourceContext === 'profile'
      ? 'Saved. Lifelog is organizing profile details...'
      : sourceContext === 'medication'
        ? 'Saved. Organizing medication details...'
        : 'Saved. Lifelog is adding this to the timeline...',
  };
};

export const getCareSourceTitle = (source = {}) => (
  source.title
  || source.sourceTitle
  || source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || (formatCareSourceRecordedAt(source)
    ? `Note from ${formatCareSourceRecordedAt(source)}`
    : 'Care note')
);

export const getCareSourceActivityTitle = (source = {}) => {
  const explicitTitle = String(
    source.title
    || source.sourceTitle
    || source.careDocumentTitle
    || source.sourceMetadata?.fileName
    || source.metadata?.source?.fileName
    || source.fileName
    || ''
  ).trim();
  const meaningfulExplicitTitle = explicitTitle
    && !/^note from\b/i.test(explicitTitle)
    && !/^care (?:note|source)$/i.test(explicitTitle);
  if (meaningfulExplicitTitle) {
    const cleanTitle = explicitTitle.replace(/\.[a-z0-9]{2,5}$/i, '').trim();
    if (/^communication update$/i.test(cleanTitle)) return 'Communication';
    return cleanTitle.slice(0, 56);
  }

  const text = getCareSourceOriginalText(source).toLowerCase();
  if (/\bmedicat(?:ion|ions|ed)\b|\bdose\b|\bprescription\b/.test(text)) return 'Medication update';
  if (/\btherapy\b.*\brecommend|\btherapist\b.*\brecommend/.test(text)) return 'Therapy recommendation';
  if (/\bbus\b.*\b(?:incident|upset|meltdown|behavior|happened)\b/.test(text)) return 'Bus incident';
  if (/\bmorning routine\b|\bgetting ready\b|\bbefore school\b/.test(text)) return 'Morning routine';
  if (/\bbedtime\b|\bbedtime routine\b|\bgoing to bed\b/.test(text)) return 'Bedtime';
  if (
    /\bcommunicat|\brespond|\banswer|\bspeak|\bwords?\b/.test(text)
    || (source.suggestedCareKnowledgeItems || []).some(
      (item) => item.profileChapterId === 'communication'
    )
  ) return 'Communication';
  if (/\bhealth\b|\bdoctor\b|\bpediatrician\b|\bmedical\b|\bsymptom\b/.test(text)) return 'Health update';
  if (text) {
    const firstThought = getCareSourceOriginalText(source)
      .replace(/\s+/g, ' ')
      .split(/[.!?\n]/)[0]
      .trim();
    if (firstThought) {
      return firstThought.length > 56
        ? `${firstThought.slice(0, 53).trimEnd()}…`
        : firstThought;
    }
  }

  return getCareSourceTitle(source);
};

export const getCareSourceOriginalText = (source = {}) => (
  source.rawText
  || source.originalText
  || source.sourceText
  || source.cleanedText
  || ''
);

export const getCareSourceTimelineEntryIds = (source = {}) => {
  const savedTimelineCards = getCareSourceSavedTimelineCards(source);
  if (savedTimelineCards.length) {
    return [...new Set(savedTimelineCards.map((card) => card.entryId).filter(Boolean))];
  }

  const careSourceIds = source.metadata?.careSourceProcessing?.timelineEntryIds;
  const captureFirstIds = source.metadata?.captureFirst?.timelineEntryIds;

  if (Array.isArray(careSourceIds)) return careSourceIds.filter(Boolean);
  if (Array.isArray(captureFirstIds)) return captureFirstIds.filter(Boolean);
  return [];
};

const ALLERGY_LIKE_PATTERN = /\b(?:can(?:not|'?t) have|can't have|cannot have|reaction|reacted|allerg(?:y|ies|ic)|anaphylaxis|anaphylactic|flagged(?: it)?|flagged by (?:a |the )?(?:nurse|doctor|provider)|nurse flagged|doctor flagged)\b/i;

export const getCareSourceProfileSuggestions = (source = {}) => {
  const suggestions = source.suggestedProfileUpdates || {};
  if (!suggestions || typeof suggestions !== 'object') return [];

  const result = [];
  const about = String(suggestions.about || '').trim();
  if (about) {
    result.push({ key: 'about', label: 'About', items: [about] });
  }

  const safetyConsiderations = (Array.isArray(suggestions.safetyConsiderations)
    ? suggestions.safetyConsiderations
    : [suggestions.safetyConsiderations])
    .map((item) => String(item || '').trim())
    .filter((item) => item && ALLERGY_LIKE_PATTERN.test(item));

  if (safetyConsiderations.length) {
    result.push({ key: 'safetyConsiderations', label: 'Safety', items: safetyConsiderations });
  }

  return result;
};

const LEGACY_PROFILE_KNOWLEDGE_DESTINATIONS = Object.freeze({
  earlyWarningSigns: { chapterId: 'watch_for', knowledgeType: 'baseline' },
  thingsThatUsuallyHelp: { chapterId: 'what_helps', knowledgeType: 'strategy' },
  thingsToAvoid: { chapterId: 'watch_for', knowledgeType: 'trigger' },
  safetyConsiderations: { chapterId: 'safety', knowledgeType: 'safety' },
  communicationStyle: { chapterId: 'communication', knowledgeType: 'support' },
  communicationNotes: { chapterId: 'communication', knowledgeType: 'support' },
  familySupportTips: { chapterId: 'what_helps', knowledgeType: 'strategy' },
  schoolTherapySuggestions: { chapterId: 'health_care', knowledgeType: 'instruction' },
});

const getLegacyProfileKnowledgeSuggestions = (source = {}) => (
  Object.entries(LEGACY_PROFILE_KNOWLEDGE_DESTINATIONS).flatMap(([profileField, destination]) => {
    const rawValue = source.suggestedProfileUpdates?.[profileField];
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    return values
      .map((value, index) => {
        const content = String(value || '').trim();
        if (!content) return null;
        return {
          clientId: `legacy-profile:${profileField}:${index}`,
          knowledgeType: destination.knowledgeType,
          profileField,
          profileChapterId: destination.chapterId,
          title: content,
          content,
          sourceText: content,
          confidence: source.profileSuggestionSummary?.confidence || 'low',
          metadata: {},
        };
      })
      .filter(Boolean);
  })
);

export const getCareSourceKnowledgeSuggestions = (source = {}) => {
  const suggestions = source.suggestedCareKnowledgeItems || [];
  const normalizedSuggestions = Array.isArray(suggestions) ? suggestions : [];

  const currentSuggestions = normalizedSuggestions
    .map((item, index) => {
      const profileField = String(item?.profileField || '').trim();
      const profileChapterId = String(item?.profileChapterId || '').trim();
      const customFactType = String(item?.customFactType || '').trim();
      return {
        clientId: String(item?.clientId || item?.id || `knowledge-${index + 1}`).trim(),
        knowledgeType: String(item?.knowledgeType || 'other').trim() || 'other',
        ...(customFactType ? { customFactType } : {}),
        ...(profileField ? { profileField } : {}),
        ...(profileChapterId ? { profileChapterId } : {}),
        title: String(item?.title || '').trim(),
        content: String(item?.content || '').trim(),
        sourceText: String(item?.sourceText || '').trim(),
        confidence: String(item?.confidence || 'low').trim() || 'low',
        metadata: item?.metadata && typeof item.metadata === 'object' && !Array.isArray(item.metadata)
          ? item.metadata
          : {},
      };
    })
    .filter((item) => item.title && item.content);

  const seen = new Set(currentSuggestions.map((item) => (
    String(item.content || '').trim().toLowerCase()
  )));
  const legacySuggestions = getLegacyProfileKnowledgeSuggestions(source)
    .filter((item) => {
      const key = item.content.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return [...currentSuggestions, ...legacySuggestions];
};

const parseLocalDate = (value = '') => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getSuggestedRecords = (source = {}) => {
  if (Array.isArray(source.extractionResult?.suggestedRecords)) {
    return source.extractionResult.suggestedRecords;
  }

  if (Array.isArray(source.metadata?.suggestedRecords)) {
    return source.metadata.suggestedRecords;
  }

  return [];
};

const suggestedRecordNeedsReview = (suggestedRecord = {}) => Boolean(
  suggestedRecord.needsReview
  || suggestedRecord.reviewState === 'needs_review'
  || suggestedRecord.record?.needsReview
  || suggestedRecord.record?.reviewState === 'needs_review'
  || suggestedRecord.record?.captureFirstReviewState === 'needs_review'
  || suggestedRecord.record?.metadata?.captureFirst?.needsReview
);

const getSuggestedRecordLocalDate = (suggestedRecord = {}) => (
  suggestedRecord.record?.localDate
  || suggestedRecord.localDate
  || ''
);

export const getCareSourceReviewTarget = (source = {}) => {
  const savedTimelineCards = getCareSourceSavedTimelineCards(source);
  const entryIds = getCareSourceTimelineEntryIds(source);
  const pointerEntryIds = getCareSourceTimelinePointerEntryIds(source);
  const savedTimelineDateKeys = Array.isArray(source.savedTimelineDateKeys)
    ? source.savedTimelineDateKeys.filter(Boolean)
    : [];
  const pointerTimelineDateKeys = getCareSourceTimelinePointerDateKeys(source);
  const suggestedRecords = getSuggestedRecords(source);
  const reviewRecordIndex = suggestedRecords.findIndex(suggestedRecordNeedsReview);
  const targetIndex = reviewRecordIndex >= 0 ? reviewRecordIndex : 0;
  const targetRecord = suggestedRecords[targetIndex] || {};
  const firstDatedRecord = suggestedRecords.find(getSuggestedRecordLocalDate);
  const localDate = savedTimelineCards[targetIndex]?.localDate
    || savedTimelineCards[0]?.localDate
    || savedTimelineDateKeys[targetIndex]
    || savedTimelineDateKeys[0]
    || pointerTimelineDateKeys[targetIndex]
    || pointerTimelineDateKeys[0]
    || getSuggestedRecordLocalDate(targetRecord)
    || getSuggestedRecordLocalDate(firstDatedRecord);

  return {
    entryId: savedTimelineCards[targetIndex]?.entryId || savedTimelineCards[0]?.entryId || entryIds[targetIndex] || entryIds[0] || pointerEntryIds[targetIndex] || pointerEntryIds[0] || '',
    date: parseLocalDate(localDate),
  };
};

export const canProcessCareSourceNow = (source = {}) => {
  if (getCareSourceTimelineEntryIds(source).length) return false;
  if (getCareSourceTimelinePointerCount(source) > 0) return false;
  if (getCareSourceSavedRecordCount(source) > 0) return false;

  return didProcessingFailBeforeSaving(source);
};

export const getCareSourceProcessActionLabel = (source = {}) => {
  return didProcessingFailBeforeSaving(source) ? 'Retry' : '';
};

export const shouldRememberCareSourceActivity = (source = {}) => {
  const status = getCareSourceStatusValue(source);
  const timelineActivityCount = getCareSourceTimelineActivityCount(source);

  return Boolean(
    source.id
    && status !== 'deleted'
    && (
      !COMPLETED_RECEIPT_STATUSES.has(status)
      || (FAILED_REVIEW_STATUSES.has(status) && timelineActivityCount < 1)
    )
  );
};

export const shouldShowCareSourceInActivity = (source = {}, {
  rememberedSourceIds = [],
} = {}) => {
  const status = getCareSourceStatusValue(source);
  const timelineActivityCount = getCareSourceTimelineActivityCount(source);

  if (status === 'deleted') return false;
  if (source.sourceActivityDismissedAt || source.dismissedByUser) return false;
  if (getProcessingReceipt(source).failed) return true;
  if (!COMPLETED_RECEIPT_STATUSES.has(status)) return true;
  if (FAILED_REVIEW_STATUSES.has(status) && timelineActivityCount < 1) return true;

  return rememberedSourceIds.includes(source.id);
};
